import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DialogData } from 'app/core/interfaces/dialog';

export interface IdentityVerificationResponse {
  url: string;
  base64_qrcode: string;
  expires_at: string;
}

@Component({
  selector: 'onboarding-pagarme',
  templateUrl: './onboarding-pagarme.component.html',
  styleUrls: ['./onboarding-pagarme.component.scss'],
})
export class OnboardingPagarmeComponent implements OnInit, OnDestroy {
  public verificationData: IdentityVerificationResponse | null = null;
  public qrCodeUrl = '';
  public expirationText = '';
  public remainingTime = '';
  public copied = false;
  public expired = false;

  private expirationInterval?: ReturnType<typeof setInterval>;
  private copyFeedbackTimeout?: ReturnType<typeof setTimeout>;

  constructor(@Inject(MAT_DIALOG_DATA) public data: DialogData & { loading?: boolean }) {}

  ngOnInit(): void {
    if (this.data?.param) {
      this.loadVerificationData(this.data.param);
    }
  }

  public loadVerificationData(data: IdentityVerificationResponse): void {
    this.verificationData = data;
    this.qrCodeUrl = this.createQrCodeUrl(data.base64_qrcode);
    this.expirationText = this.formatExpirationDate(data.expires_at);
    this.startExpirationTimer();
  }

  public async copyUrl(): Promise<void> {
    const url = this.verificationData?.url;

    if (!url) {
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        await this.fallbackCopy(url);
      }

      this.showCopiedFeedback();
    } catch (error) {
      console.error('Não foi possível copiar a URL:', error);
    }
  }

  private createQrCodeUrl(base64QrCode: string): string {
    if (!base64QrCode) {
      return '';
    }

    const normalizedBase64 = base64QrCode.replace(/^data:image\/svg\+xml;base64,/, '').replace(/\s/g, '');

    return `data:image/svg+xml;base64,${normalizedBase64}`;
  }

  private formatExpirationDate(expiresAt: string): string {
    const expirationDate = new Date(expiresAt);

    if (Number.isNaN(expirationDate.getTime())) {
      return 'Data de expiração inválida';
    }

    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
    }).format(expirationDate);
  }

  private startExpirationTimer(): void {
    if (this.expirationInterval) {
      clearInterval(this.expirationInterval);
    }

    this.updateRemainingTime();

    this.expirationInterval = setInterval(() => {
      this.updateRemainingTime();
    }, 1000);
  }

  private updateRemainingTime(): void {
    if (!this.verificationData?.expires_at) {
      return;
    }

    const expirationTimestamp = new Date(this.verificationData.expires_at).getTime();
    const difference = expirationTimestamp - Date.now();

    if (difference <= 0) {
      this.expired = true;
      this.remainingTime = 'Link expirado';

      if (this.expirationInterval) {
        clearInterval(this.expirationInterval);
      }

      return;
    }

    this.expired = false;

    const totalSeconds = Math.floor(difference / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const formattedTime = [this.pad(hours), this.pad(minutes), this.pad(seconds)].join(':');

    this.remainingTime = days > 0 ? `${days} dia${days > 1 ? 's' : ''} e ${formattedTime}` : formattedTime;
  }

  private pad(value: number): string {
    return value.toString().padStart(2, '0');
  }

  private async fallbackCopy(value: string): Promise<void> {
    const textarea = document.createElement('textarea');

    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';

    document.body.appendChild(textarea);
    textarea.select();

    const copiedSuccessfully = document.execCommand('copy');

    document.body.removeChild(textarea);

    if (!copiedSuccessfully) {
      throw new Error('O navegador não permitiu copiar o conteúdo.');
    }
  }

  private showCopiedFeedback(): void {
    this.copied = true;

    if (this.copyFeedbackTimeout) {
      clearTimeout(this.copyFeedbackTimeout);
    }

    this.copyFeedbackTimeout = setTimeout(() => {
      this.copied = false;
    }, 2500);
  }

  ngOnDestroy(): void {
    if (this.expirationInterval) {
      clearInterval(this.expirationInterval);
    }

    if (this.copyFeedbackTimeout) {
      clearTimeout(this.copyFeedbackTimeout);
    }
  }
}
